import Dialog from "../../components/General/ConfirmableDialog";
import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { Visibility as VisibilityIcon } from "@mui/icons-material";
import { format, parseISO } from "date-fns";
import { Company, getCompanies } from "../../api/companyService";
import {
  getInvoices,
  getInvoice,
  payInvoice,
  generateAgingReport,
} from "../../api/invoiceService";
import useAuth from "../../hooks/useAuth";
import {
  InvoiceSummary,
  InvoiceDetail,
  PayInvoiceRequest,
} from "../../interfaces/invoice";

const PAYMENT_METHODS = [
  "STATEMENT",
  "ACH",
  "ZELLE",
  "CASH",
  "QUICKBOOK_PAYMENT",
];

const Invoices: React.FC = () => {
  const { token } = useAuth();
  const [invoices, setInvoices] = useState<InvoiceSummary[]>([]);
  const [showOnlyOpen, setShowOnlyOpen] = useState(true);
  const [selectedInvoice, setSelectedInvoice] = useState<InvoiceDetail | null>(
    null,
  );
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isAgingDialogOpen, setIsAgingDialogOpen] = useState(false);
  const [isPaymentDialogOpen, setIsPaymentDialogOpen] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] =
    useState<string>("");
  const [asOfDate, setAsOfDate] = useState<Date | null>(new Date());
  const [selectedIssuer, setSelectedIssuer] = useState<Company | null>(null);
  const [selectedBillTo, setSelectedBillTo] = useState<Company | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [paymentAmount, setPaymentAmount] = useState<number>(0);
  const [paymentDate, setPaymentDate] = useState<Date | null>(new Date());

  useEffect(() => {
    loadInvoices();
    loadCompanies();
  }, [showOnlyOpen]);

  const loadInvoices = async () => {
    try {
      const data = await getInvoices(token, undefined, undefined, showOnlyOpen);
      setInvoices(data);
    } catch (error) {
      console.error("Failed to load invoices:", error);
    }
  };

  const loadCompanies = async () => {
    try {
      const data = await getCompanies(token);
      setCompanies(data);
    } catch (error) {
      console.error("Failed to load companies:", error);
    }
  };

  const handleViewInvoice = async (id: string) => {
    try {
      const invoice = await getInvoice(token, id);
      setSelectedInvoice(invoice);
      setIsDialogOpen(true);
    } catch (error) {
      console.error("Failed to load invoice details:", error);
    }
  };

  const handlePayInvoice = async () => {
    if (
      !selectedInvoice ||
      !selectedPaymentMethod ||
      paymentAmount <= 0 ||
      !paymentDate
    )
      return;

    try {
      const payment: PayInvoiceRequest = {
        amount: paymentAmount,
        method: selectedPaymentMethod,
        paymentDate: format(paymentDate, "yyyy-MM-dd'T'HH:mm:ss.SSSxxx"),
      };
      await payInvoice(token, selectedInvoice.id, payment);
      setIsPaymentDialogOpen(false);
      setIsDialogOpen(false);
      loadInvoices();
    } catch (error) {
      console.error("Failed to pay invoice:", error);
    }
  };

  const handleOpenPaymentDialog = () => {
    if (selectedInvoice) {
      setPaymentAmount(selectedInvoice.balance);
    }
    setPaymentDate(new Date());
    setSelectedPaymentMethod("");
    setIsPaymentDialogOpen(true);
  };

  const handleGenerateAgingReport = async () => {
    if (!asOfDate || !selectedIssuer) return;

    try {
      await generateAgingReport(token, {
        asOf: asOfDate.toISOString(),
        issuerId: selectedIssuer.id,
        billToId: selectedBillTo?.id,
      });
      setIsAgingDialogOpen(false);
    } catch (error) {
      console.error("Failed to generate aging report:", error);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return format(parseISO(dateString), "MM/dd/yyyy");
    } catch (error) {
      console.error("Error formatting date:", error);
      return dateString;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          flexWrap: { xs: "wrap", lg: "nowrap" },
          display: "flex",
          justifyContent: "space-between",
          mb: 3,
        }}
      >
        <Box
          sx={{
            flexWrap: { xs: "wrap", lg: "nowrap" },
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Typography variant="h4">Invoices</Typography>
          <FormControlLabel
            control={
              <Switch
                checked={showOnlyOpen}
                onChange={(e) => setShowOnlyOpen(e.target.checked)}
              />
            }
            label="Show only open invoices"
          />
        </Box>
        <Button
          variant="contained"
          color="primary"
          onClick={() => setIsAgingDialogOpen(true)}
        >
          Generate Aging Report
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Company</TableCell>
              <TableCell>Unit Number</TableCell>
              <TableCell>Asset Type</TableCell>
              <TableCell>Invoice #</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Due Date</TableCell>
              <TableCell>Total</TableCell>
              <TableCell>Balance</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {invoices.map((invoice) => (
              <TableRow key={invoice.id}>
                <TableCell>{invoice.billToCompanyName}</TableCell>
                <TableCell>{invoice.unitNumber}</TableCell>
                <TableCell>{invoice.assetType}</TableCell>
                <TableCell>{invoice.invoiceNumber}</TableCell>
                <TableCell>{formatDate(invoice.invoiceDate)}</TableCell>
                <TableCell>{formatDate(invoice.dueDate)}</TableCell>
                <TableCell>${invoice.total.toFixed(2)}</TableCell>
                <TableCell>${invoice.balance.toFixed(2)}</TableCell>
                <TableCell>
                  <IconButton onClick={() => handleViewInvoice(invoice.id)}>
                    <VisibilityIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Invoice Details Dialog */}
      <Dialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Invoice Details</DialogTitle>
        <DialogContent>
          {selectedInvoice && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6">
                Invoice #{selectedInvoice.invoiceNumber}
              </Typography>
              <Typography>
                Date: {formatDate(selectedInvoice.invoiceDate)}
              </Typography>
              <Typography>
                Due Date: {formatDate(selectedInvoice.dueDate)}
              </Typography>
              <Typography>
                Subtotal: ${selectedInvoice.subtotal.toFixed(2)}
              </Typography>
              <Typography>Tax: ${selectedInvoice.tax.toFixed(2)}</Typography>
              <Typography>
                Total: ${selectedInvoice.total.toFixed(2)}
              </Typography>
              <Typography>
                Amount Paid: ${selectedInvoice.amountPaid.toFixed(2)}
              </Typography>
              <Typography>
                Balance: ${selectedInvoice.balance.toFixed(2)}
              </Typography>

              <Typography variant="h6" sx={{ mt: 2 }}>
                Items
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Description</TableCell>
                      <TableCell>Quantity</TableCell>
                      <TableCell>Rate</TableCell>
                      <TableCell>Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedInvoice.items.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>{item.description}</TableCell>
                        <TableCell>{item.quantity}</TableCell>
                        <TableCell>${item.rate.toFixed(2)}</TableCell>
                        <TableCell>
                          $
                          {(item.amount || item.rate * item.quantity).toFixed(
                            2,
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {selectedInvoice.payments.length > 0 && (
                <>
                  <Typography variant="h6" sx={{ mt: 2 }}>
                    Payments
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Date</TableCell>
                          <TableCell>Amount</TableCell>
                          <TableCell>Method</TableCell>
                          <TableCell>User</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedInvoice.payments.map((payment, index) => (
                          <TableRow key={index}>
                            <TableCell>
                              {formatDate(payment.paymentDate)}
                            </TableCell>
                            <TableCell>
                              ${payment.paymentAmount.toFixed(2)}
                            </TableCell>
                            <TableCell>{payment.paymentMethod}</TableCell>
                            <TableCell>{payment.createdBy}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {selectedInvoice && selectedInvoice.balance > 0 && (
            <Button
              onClick={handleOpenPaymentDialog}
              color="primary"
              variant="contained"
            >
              Pay Invoice
            </Button>
          )}
          <Button onClick={() => setIsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Payment Method Dialog */}
      <Dialog
        open={isPaymentDialogOpen}
        onClose={() => setIsPaymentDialogOpen(false)}
      >
        <DialogTitle>Select Payment Method</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Payment Date"
                value={paymentDate}
                onChange={(newValue) => setPaymentDate(newValue)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: true,
                  },
                }}
              />
            </LocalizationProvider>
            <TextField
              label="Payment Amount"
              type="number"
              value={paymentAmount}
              onChange={(e) => setPaymentAmount(Number(e.target.value))}
              inputProps={{
                min: 0,
                max: selectedInvoice?.balance || 0,
                step: 0.01,
              }}
              fullWidth
              required
              error={
                paymentAmount <= 0 ||
                paymentAmount > (selectedInvoice?.balance || 0)
              }
              helperText={
                paymentAmount <= 0
                  ? "Amount must be greater than 0"
                  : paymentAmount > (selectedInvoice?.balance || 0)
                    ? "Amount cannot exceed invoice balance"
                    : ""
              }
            />
            <FormControl fullWidth>
              <InputLabel>Payment Method</InputLabel>
              <Select
                value={selectedPaymentMethod}
                onChange={(e) => setSelectedPaymentMethod(e.target.value)}
                label="Payment Method"
              >
                {PAYMENT_METHODS.map((method) => (
                  <MenuItem key={method} value={method}>
                    {method.replace("_", " ")}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {selectedInvoice && (
              <Typography variant="body2" color="text.secondary">
                Invoice Balance: ${selectedInvoice.balance.toFixed(2)}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsPaymentDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handlePayInvoice}
            color="primary"
            variant="contained"
            disabled={
              !selectedPaymentMethod ||
              paymentAmount <= 0 ||
              paymentAmount > (selectedInvoice?.balance || 0) ||
              !paymentDate
            }
          >
            Confirm Payment
          </Button>
        </DialogActions>
      </Dialog>

      {/* Aging Report Dialog */}
      <Dialog
        open={isAgingDialogOpen}
        onClose={() => setIsAgingDialogOpen(false)}
      >
        <DialogTitle>Generate Aging Report</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="As of Date"
                value={asOfDate}
                onChange={(newValue) => setAsOfDate(newValue)}
              />
            </LocalizationProvider>
            <Autocomplete
              options={companies}
              getOptionLabel={(option) => option.name}
              value={selectedIssuer}
              onChange={(_, newValue) => setSelectedIssuer(newValue)}
              renderInput={(params) => (
                <TextField {...params} label="Issuer" required />
              )}
            />
            <Autocomplete
              options={companies}
              getOptionLabel={(option) => option.name}
              value={selectedBillTo}
              onChange={(_, newValue) => setSelectedBillTo(newValue)}
              renderInput={(params) => (
                <TextField {...params} label="Bill To (Optional)" />
              )}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsAgingDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleGenerateAgingReport}
            color="primary"
            variant="contained"
            disabled={!asOfDate || !selectedIssuer}
          >
            Generate
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Invoices;
